/**
 * Hooks TanStack Query — CRM suivi candidatures côté entreprise (V13).
 *
 * Hooks :
 *  - useCompanyApplications(filters)  — GET /entreprise/applications (liste globale)
 *  - useCompanyTracking(id)           — GET /entreprise/applications/:id/tracking
 *  - useUpdateCompanyTracking()       — PATCH /entreprise/applications/:id/tracking
 *  - useAddCompanyInteraction()       — POST /entreprise/applications/:id/interactions
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import type {
  RecruitmentStatus,
  ApplicationChannel,
  InteractionType,
  UpdateCompanyTrackingInput,
} from '@lecomptoir/shared';

// ─── Types ───────────────────────────────────────────────────────────────────

export type Interaction = {
  id: string;
  type: InteractionType;
  authorRole: string;
  description: string | null;
  occurredAt: string;
  createdAt: string;
};

export type CompanyApplicationSummary = {
  id: string;
  recruitmentStatus: RecruitmentStatus;
  /** ISO string ou null (historique inconnu pré-V13 → pas d'alerte stagnation). */
  recruitmentStatusChangedAt: string | null;
  companyNextAction: string | null;
  companyNextActionAt: string | null;
  applicationChannel: ApplicationChannel;
  documentsSent: string[];
  createdAt: string;
  updatedAt: string;
  jobOffer: { id: string; title: string; contractType: string };
  candidate: {
    id: string;
    nom: string;
    prenom: string;
    email: string;
    hasCv: boolean;
    /** Formation principale (badge Kanban). Null pour les candidats externes. */
    formation: { label: string; code: string } | null;
  } | null;
  messageCount: number;
  /** Messages non-lus par l'utilisateur entreprise courant. */
  unreadCount: number;
  interactionCount: number;
};

export type CompanyTrackingDetail = {
  id: string;
  recruitmentStatus: RecruitmentStatus;
  applicationChannel: ApplicationChannel;
  documentsSent: string[];
  companyNextAction: string | null;
  companyNextActionAt: string | null;
  companyNotes: string | null;
  message: string | null;
  createdAt: string;
  updatedAt: string;
  jobOffer: { id: string; title: string; contractType: string };
  student: { userId: string; nom: string; prenom: string; telephone: string | null; user: { email: string } } | null;
  interactions: Interaction[];
};

export type Pagination = { total: number; page: number; pageSize: number; pages: number };

export type CompanyApplicationsFilters = {
  q?: string;
  recruitmentStatus?: RecruitmentStatus;
  overdueOnly?: boolean;
  page?: number;
  pageSize?: number;
};

// ─── Hooks ───────────────────────────────────────────────────────────────────

/** Total messages non-lus toutes candidatures — utilisé par le badge sidebar. */
export function useCompanyUnreadCount() {
  return useQuery({
    queryKey: ['entreprise', 'unread-count'],
    queryFn: () =>
      apiFetch<{ totalUnread: number }>('/entreprise/applications/unread-count')
        .then((r) => r.totalUnread)
        .catch(() => 0),
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
}

export function useCompanyApplications(filters: CompanyApplicationsFilters = {}) {
  const params = new URLSearchParams();
  if (filters.q) params.set('q', filters.q);
  if (filters.recruitmentStatus) params.set('recruitmentStatus', filters.recruitmentStatus);
  if (filters.overdueOnly) params.set('overdueOnly', 'true');
  if (filters.page) params.set('page', String(filters.page));
  if (filters.pageSize) params.set('pageSize', String(filters.pageSize));

  const qs = params.toString();
  return useQuery({
    queryKey: ['entreprise', 'applications', filters],
    queryFn: () =>
      apiFetch<{
        applications: CompanyApplicationSummary[];
        pagination: Pagination;
        overdueCount: number;
      }>(`/entreprise/applications${qs ? `?${qs}` : ''}`),
    staleTime: 30_000,
  });
}

export function useCompanyTracking(applicationId: string | null) {
  return useQuery({
    queryKey: ['entreprise', 'tracking', applicationId],
    queryFn: () =>
      apiFetch<{ application: CompanyTrackingDetail }>(
        `/entreprise/applications/${applicationId}/tracking`,
      ).then((r) => r.application),
    enabled: !!applicationId,
    staleTime: 30_000,
  });
}

export function useUpdateCompanyTracking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdateCompanyTrackingInput;
    }) =>
      apiFetch(`/entreprise/applications/${id}/tracking`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: ['entreprise', 'tracking', id] });
      void qc.invalidateQueries({ queryKey: ['entreprise', 'applications'] });
    },
  });
}

export function useAddCompanyInteraction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      type,
      description,
    }: {
      id: string;
      type: InteractionType;
      description?: string | null;
    }) =>
      apiFetch(`/entreprise/applications/${id}/interactions`, {
        method: 'POST',
        body: JSON.stringify({ type, description, occurredAt: new Date().toISOString() }),
      }),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: ['entreprise', 'tracking', id] });
    },
  });
}
